// src/pages/bloomterminal/components/dashboard/WidgetDashboard.jsx
import React, { useMemo, useCallback, useEffect, useState, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Responsive, useContainerWidth, verticalCompactor } from 'react-grid-layout';
import WidgetContainer from './WidgetContainer';
import { CHART_REGISTRY, isChartRestricted } from '../../config/chartRegistry';
import { useUser } from '../../../context/UserContext';
import { useTheme } from '../../../context/ThemeContext';
import { getChartTheme } from '../../utils/chartTheme';
import './dashboard.css';

// Grid configuration - High granularity for free-form resizing
// Using 48 columns and 20px row height for smooth, near-pixel-perfect resizing
const BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
const COLS = { lg: 48, md: 40, sm: 24, xs: 16, xxs: 8 };
const ROW_HEIGHT = 20;
const MARGIN = [16, 16];
const CONTAINER_PADDING = [0, 0];

const FOCUSED_WIDGET_Z = 2;
const DEFAULT_WIDGET_Z = 1;

/**
 * WidgetDashboard - Main grid container for draggable, resizable chart widgets
 *
 * Uses react-grid-layout for drag/resize functionality
 * Layout state is managed by useDashboardLayout hook in parent
 * Vertical compaction reflows widgets on drag/resize so the grid has no blank gaps
 * Each widget fetches its own data using individual API hooks
 * @see https://github.com/react-grid-layout/react-grid-layout
 */
const WidgetDashboard = ({
  layout,
  apiParams,
  onLayoutChange,
  onRemoveWidget,
}) => {
  const { t } = useTranslation();
  const { tier, chartRestrictions } = useUser();
  const { isDark } = useTheme();
  const theme = getChartTheme(isDark);
  const { width, containerRef, mounted } = useContainerWidth();
  const [focusedWidgetId, setFocusedWidgetId] = useState(null);
  const isInteractingRef = useRef(false);
  // Force-render counter used to flushSync between resize ticks. See handleResize.
  const [, forceRenderTick] = useState(0);
  // Coalesce flushes to one per microtask so we never schedule multiple flushes
  // for events that fire inside the same JS task (e.g. several pointermoves
  // dispatched before the next render).
  const flushScheduledRef = useRef(false);
  const activeWidgets = layout.activeWidgets || [];
  const layouts = layout.layouts || {};

  useEffect(() => {
    setFocusedWidgetId((id) => (id && !activeWidgets.includes(id) ? null : id));
  }, [activeWidgets]);

  // Vertical-only window auto-scroll while dragging a widget near the top
  // or bottom of the viewport.
  //
  // We intentionally do NOT scroll horizontally and we do NOT auto-scroll
  // during resize. dom-autoscroller (which we used previously) always scrolls
  // both axes and offers no way to opt out of horizontal scroll, which made
  // the page slide to the right during east-handle resizes and never recover.
  useEffect(() => {
    const MARGIN = 60;
    const MAX_SPEED = 14;
    let pointerY = 0;
    let pointerDown = false;
    let rafId = null;

    const isDragging = () =>
      !!document.querySelector('.react-draggable-dragging');

    const tick = () => {
      rafId = null;
      if (!pointerDown || !isDragging()) return;

      const viewportH = window.innerHeight;
      let dy = 0;
      if (pointerY < MARGIN) {
        dy = Math.max(-1, (pointerY - MARGIN) / MARGIN) * MAX_SPEED;
      } else if (pointerY > viewportH - MARGIN) {
        dy = Math.min(1, (pointerY - (viewportH - MARGIN)) / MARGIN) * MAX_SPEED;
      }
      if (dy !== 0) {
        window.scrollBy(0, dy);
      }
      rafId = requestAnimationFrame(tick);
    };

    const schedule = () => {
      if (rafId == null) rafId = requestAnimationFrame(tick);
    };

    const onPointerMove = (e) => {
      pointerY = e.clientY;
      if (pointerDown && isDragging()) schedule();
    };
    const onPointerDown = () => {
      pointerDown = true;
      schedule();
    };
    const onPointerUp = () => {
      pointerDown = false;
      if (rafId != null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, []);

  // Clear widget focus when clicking outside the dashboard
  useEffect(() => {
    const handlePointerDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setFocusedWidgetId(null);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [containerRef]);

  // Add min constraints to layout items (no maxW to allow free resizing)
  const layoutsWithConstraints = useMemo(() => {
    const result = {};

    Object.keys(layouts).forEach((breakpoint) => {
      result[breakpoint] = (layouts[breakpoint] || [])
        .filter((item) => activeWidgets.includes(item.i))
        .map((item) => {
          const chartConfig = CHART_REGISTRY[item.i];
          const configuredMinW = chartConfig?.minSize?.w || 1;
          const minH = chartConfig?.minSize?.h || 1;
          const bpCols = COLS[breakpoint] ?? 48;
          const minW = Math.min(configuredMinW, bpCols);
          const w = Math.max(item.w ?? 1, minW);
          const h = Math.max(item.h ?? 1, minH);
          return {
            ...item,
            w,
            h,
            minW,
            minH,
          };
        });
    });

    return result;
  }, [layouts, activeWidgets]);

  const handleLayoutChange = useCallback(
    (currentLayout, allLayouts) => {
      if (onLayoutChange) {
        onLayoutChange(currentLayout, allLayouts);
      }
    },
    [onLayoutChange]
  );

  /**
   * Force React to commit any pending state updates synchronously.
   *
   * Why this is needed: react-resizable computes the new width on each
   * pointermove as `props.width + deltaX` (see `Resizable.js` in
   * react-resizable). With React 18 automatic batching, multiple
   * pointermoves can fire between renders, so `props.width` stays stale
   * across several ticks while `deltaX` is per-event (not cumulative).
   * The result is the new width = stale width + just-this-event's delta,
   * which makes `layout.w` oscillate (e.g. 48 → 43 → 46 → 39 → 46 → …)
   * during fast drags. The compensation react-resizable does in
   * `lastHandleRect` only covers the `'w'` and `'n'` axes, not `'e'`/`'s'`,
   * which is exactly the handle being used here.
   *
   * Charts that are expensive to render (FOB Price Pulse, Terminal Price
   * Pulse, Volume by Origin all use `useSyncedChartScroll` + horizontally
   * scrollable Recharts) render slowly enough that React falls behind, so
   * the staleness window grows and the oscillation gets visibly worse.
   *
   * Scheduling a `flushSync` in a microtask runs it AFTER react-grid-layout
   * has called its internal `setLayout`/`setActiveDrag` for this resize
   * tick (those happen synchronously after our `onResize` callback returns).
   * That commits the render, so the next pointermove sees a fresh
   * `props.width` and the math stays consistent.
   */
  const scheduleSyncFlush = useCallback(() => {
    if (flushScheduledRef.current) return;
    flushScheduledRef.current = true;
    queueMicrotask(() => {
      flushScheduledRef.current = false;
      try {
        flushSync(() => {
          forceRenderTick((t) => (t + 1) | 0);
        });
      } catch {
        // flushSync can throw if called from inside a render (it shouldn't
        // be possible from a microtask, but be defensive).
      }
    });
  }, []);

  const handleInteractionStart = useCallback(() => {
    isInteractingRef.current = true;
  }, []);

  const handleResize = useCallback(() => {
    scheduleSyncFlush();
  }, [scheduleSyncFlush]);

  const handleDrag = useCallback(() => {
    scheduleSyncFlush();
  }, [scheduleSyncFlush]);

  const handleInteractionStop = useCallback(() => {
    isInteractingRef.current = false;
  }, []);

  // Empty state
  if (activeWidgets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[400px] text-[#828382] dark:text-gray-400">
        <p className="text-lg mb-2">{t('charts.noWidgets')}</p>
        <p className="text-sm">{t('charts.noWidgetsHint')}</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="widget-dashboard pb-8"
      style={{ minHeight: '100vh', overflow: 'visible' }}
    >
      {mounted && (
        <Responsive
          className="layout"
          layouts={layoutsWithConstraints}
          breakpoints={BREAKPOINTS}
          cols={COLS}
          rowHeight={ROW_HEIGHT}
          margin={MARGIN}
          containerPadding={CONTAINER_PADDING}
          width={width}
          dragConfig={{
            enabled: true,
            bounded: false,
            handle: '.widget-drag-handle',
            cancel: ".widget-chart-content, .leaflet-container, .widget-no-drag, input[type='range']",
          }}
          resizeConfig={{
            enabled: true,
            handles: ['sw', 'nw', 'se', 'ne'],
          }}
          compactor={verticalCompactor}
          onLayoutChange={handleLayoutChange}
          onDragStart={handleInteractionStart}
          onDrag={handleDrag}
          onDragStop={handleInteractionStop}
          onResizeStart={handleInteractionStart}
          onResize={handleResize}
          onResizeStop={handleInteractionStop}
          useCSSTransforms={true}
          isBounded={false}
          autoSize={true}
        >
          {activeWidgets.map((widgetId) => {
            const chartConfig = CHART_REGISTRY[widgetId];
            if (!chartConfig) return null;

            const isRestricted = isChartRestricted(widgetId, tier, chartRestrictions);

            return (
              <div
                key={widgetId}
                className="p-1 h-full"
                data-widget-id={widgetId}
                style={{
                  position: 'relative',
                  zIndex: focusedWidgetId === widgetId ? FOCUSED_WIDGET_Z : DEFAULT_WIDGET_Z,
                }}
                onMouseEnter={() => setFocusedWidgetId(widgetId)}
                onPointerDownCapture={() => setFocusedWidgetId(widgetId)}
              >
                <div
                  className="flex h-full w-full min-h-0 flex-col rounded-lg border border-bloom-green/40"
                  style={{ backgroundColor: theme.widgetBg }}
                >
                  {isRestricted && (
                    <div className="shrink-0 border-b border-[#E8EBE9]/60 px-3 py-3 dark:border-[#3a3c3b]/60">
                      <h3 className="text-sm font-semibold text-[#282A29] dark:text-white">
                        {t(chartConfig.titleKey)}
                      </h3>
                    </div>
                  )}
                  <div className="relative min-h-0 flex-1">
                    <WidgetContainer
                      chartConfig={chartConfig}
                      apiParams={apiParams}
                      onRemove={() => onRemoveWidget(widgetId)}
                      locked={isRestricted}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </Responsive>
      )}
    </div>
  );
};

export default WidgetDashboard;
